import { DigitsGroupingPipe } from '../pipes';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';

import { AlertMessageComponent } from '../alert-message/alert-message.component';
import { CommonModule } from '@angular/common';
import { CounterComponent } from '../counter/counter.component';
import { LightCarouselComponent } from '@light-carousel';
import { MatIcon } from '@angular/material/icon';
import { ProductCart, usdToPoints } from '@models';
import { ConfigurationService } from '@core';
import { CartOldPriceComponent } from '../cart-old-price/cart-old-price.component';

@Component({
  selector: 'lib-cart-item',
  templateUrl: './cart-item.component.html',
  styleUrl: './cart-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    CounterComponent,
    MatIcon,
    AlertMessageComponent,
    LightCarouselComponent,
    CartOldPriceComponent,
    DigitsGroupingPipe,
  ],
  host: {
    '[class.mobile-mode]': 'mode() === "mobile"',
  },
})
export class CartItemComponent {
  protected readonly rewardName = inject(ConfigurationService).rewardName;
  product = input<ProductCart>();
  loading = input<boolean>();
  mode = input<'mobile' | 'tablet'>();
  /**
   * FCR 19.1 — presentation flag: the monthly per-SKU limit is reached for this item.
   * Disables the "+" button (caps the counter at the current quantity). Logic wired separately.
   */
  orderLimitReached = input<boolean>(false);
  /**
   * RID-1278 — upper cap for the quantity stepper (the remaining monthly allowance for this SKU).
   * Uncapped by default so the tile is never capped below the limit by accident.
   */
  maxQuantity = input<number>(Infinity);
  /**
   * FCR 19.1 — optional warning shown above the tile (AC02/AC05 limit reached,
   * AC07 "You previously ordered [##]"). Empty/undefined hides it.
   */
  limitMessage = input<string>();
  /**
   * RCP-458 DoD11 — error shown under the tile while its quantity is above the available count.
   * Empty/undefined hides it. The remaining count is never part of it (DoD13).
   */
  stockMessage = input<string>();
  /** RCP-452 — `lineNumber` targets the clicked line when a SKU spans several (pre-RCP-379 Apple carts). */
  remove = output<{ productId: number; lineNumber?: number }>();
  quantityChange = output<{ productId: number; quantity: number; lineNumber?: number }>();

  vm = computed(() => {
    const product = this.product();
    const price = product?.price ?? 0;
    const discountedPrice = product?.discountedPrice ?? 0;

    const pricePoints = usdToPoints(price);
    const discountedPricePoints = discountedPrice ? usdToPoints(discountedPrice) : 0;

    const isPromo = product?.isPromo;

    let displayNewPoints: number;
    let displayOldPoints: number | null;
    let displayNewPrice: number;
    let displayOldPrice: number | null;

    if (isPromo && discountedPrice) {
      // Price discount: show discounted price/points, old regular price/points
      displayNewPoints = discountedPricePoints;
      displayNewPrice = discountedPrice;
      displayOldPrice = price;
      displayOldPoints = pricePoints;
    } else {
      // No price discount
      displayNewPoints = pricePoints;
      displayNewPrice = price;
      displayOldPrice = null;
      displayOldPoints = null;
    }

    return {
      product: product,
      points: pricePoints,
      discountedPoints: discountedPricePoints,
      smallImagesForCards: product?.images,
      // New display values
      displayNewPoints,
      displayOldPoints,
      displayNewPrice,
      displayOldPrice,
      shouldShowOldPoints: displayOldPoints !== null,
    };
  });

  onQuantityChange(quantity: number, productId: number): void {
    this.quantityChange.emit({ productId, quantity, lineNumber: this.product()?.lineNumber });
  }

  onRemove(id: number): void {
    this.remove.emit({ productId: id, lineNumber: this.product()?.lineNumber });
  }
}
